import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useStytchB2BClient } from '@stytch/react/b2b'
import { showErrorSnackbar } from '@/utils/snackbars'

interface OAuthParams {
  client_id: string
  redirect_uri: string
  response_type: string
  scope: string
  state?: string
}

// TODO: update this when we have multiple connected apps
export default function ConnectedApp() {
  const stytch = useStytchB2BClient()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const getOAuthParams = useCallback((): OAuthParams | null => {
    const client_id = searchParams.get('client_id')
    const redirect_uri = searchParams.get('redirect_uri')
    const response_type = searchParams.get('response_type')
    const scope = searchParams.get('scope')

    if (!client_id || !redirect_uri || !response_type || !scope) {
      return null
    }

    return {
      client_id,
      redirect_uri,
      response_type,
      scope,
      state: searchParams.get('state') || undefined,
    }
  }, [searchParams])

  const startOAuthAuthorization = useCallback(
    async (params: OAuthParams) => {
      const { client_id, redirect_uri, response_type, scope } = params
      return stytch.idp.oauthAuthorizeStart({
        client_id,
        redirect_uri,
        response_type,
        scopes: scope.split(' '),
        prompt: 'consent',
      })
    },
    [stytch.idp],
  )

  const submitOAuthAuthorization = useCallback(
    async (params: OAuthParams) => {
      const { client_id, redirect_uri, response_type, scope, state } = params
      return stytch.idp.oauthAuthorizeSubmit({
        client_id,
        redirect_uri,
        response_type,
        scopes: scope.split(' '),
        prompt: 'consent',
        consent_granted: true, // hardcoded for now since we are only using for Postman
        state,
      })
    },
    [stytch.idp],
  )

  useEffect(() => {
    const initiateOAuthFlow = async () => {
      try {
        const oauthParams = getOAuthParams()
        if (!oauthParams) {
          showErrorSnackbar(
            'Invalid OAuth parameters. Please check the authorization URL.',
          )
          navigate('/')
          return
        }
        await startOAuthAuthorization(oauthParams)
        const submitResponse = await submitOAuthAuthorization(oauthParams)
        window.location.href = submitResponse.redirect_uri
      } catch {
        showErrorSnackbar(
          'Failed to authorize the application. Please try again or contact support.',
        )
        navigate('/')
      }
    }

    initiateOAuthFlow()
  }, [
    getOAuthParams,
    navigate,
    startOAuthAuthorization,
    submitOAuthAuthorization,
  ])

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={3}
      sx={{ height: '100vh' }}
    >
      <CircularProgress size={80} />
      <Typography variant="h6" color="text.secondary">
        Authorizing application...
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center', maxWidth: 400 }}
      >
        Please wait while we securely connect your account with the requested
        application.
      </Typography>
    </Stack>
  )
}
